import {
  mdiClose,
  mdiFilterVariant,
  mdiPlus,
  mdiHeartOutline,
  mdiHeart,
  mdiSortAscending,
  mdiSortDescending,
  mdiCompassOutline
} from "@mdi/js";
import Vue from "vue";
import VueI18n from "vue-i18n";
import findEvents, { findLocations } from "@/lib/find-events";
import findConfig from "@/lib/find-config";
import niceDate from "@/lib/nice-date";
import Card from "@/components/Card/Card.vue";
import Map from "@/components/Map/Map.vue";
import ListFilter from "@/components/ListFilter/ListFilter.vue";
import createLang from "@/list_lang";
import ConditionalHeader from "@/components/ConditionalHeader";
import CalendarPicker from "@/components/CalendarPicker/CalendarPicker.vue";
import ListTable from "@/components/ListTable/ListTable.vue";
import moment from "moment";

import "./List.sass";
import {
  replaceArrayWithWebContent,
  validateDate
} from "@/helpers/commonHelper";
import flatten from "lodash/flatten";
import uniqueId from "lodash/uniqueId";

// language stuff
Vue.use(VueI18n);
const i18n = createLang();

export default {
  name: "list",
  components: {
    ListTable,
    ConditionalHeader,
    Card,
    Map,
    ListFilter,
    CalendarPicker
  },
  props: {
    id: {
      type: String,
      default: "eoev-list"
    },
    initialData: Array,
    initialSettings: Object,
    initialNextPage: {
      type: Number,
      default: -1
    },
    highlight: Number,
    apiKey: String
  },
  data: () => ({
    isCreating: true, // initial creation process
    isLoading: true, // initial load?
    events: [], // list of events
    settings: {}, // loaded settings
    totalCount: 0, // total results
    countByCategory: {}, // facet count by category id
    countByFeature: {}, // facet count by feature id
    locationsWithCounts: [], // facet count by location id
    locations: [],
    nextPage: 0, // next page
    error: "",
    widgetWidth: 0, // width of complete element
    leftColWidth: 0, // width of left column
    rightColWidth: 0, // width of left column
    // view states
    showMap: false,
    showFilters: false,
    showNotepad: false,
    disableFilter: false,
    sortings: [],
    currentSorting: "moment:asc",
    outputFormat: "standard",
    accessInformation: false,
    internInstruction: false,
    internComment: false,
    showShortTitle: false,
    showShortDescription: false,
    hitsPerPage: 20,
    headersIntern: [
      {
        align: "start",
        sortable: false,
        value: "name"
      },
      { text: "Uhrzeit", value: "startTime" },
      { text: "Dauer", value: "duration" },
      { text: "Veranstaltung", value: "title" },
      { text: "Veranstaltungsort", value: "locationObj[0].title" }
    ],
    // icons
    mdiFilterVariant: mdiFilterVariant,
    mdiClose: mdiClose,
    mdiPlus: mdiPlus,
    mdiHeartOutline: mdiHeartOutline,
    mdiHeart: mdiHeart,
    mdiSortAscending: mdiSortAscending,
    mdiSortDescending: mdiSortDescending,
    mdiCompassOutline: mdiCompassOutline,
    pickerId: uniqueId()
  }),
  async mounted() {
    // load categories and featureGroups to Store
    await this.$store.dispatch("loadCategories");
    await this.$store.dispatch("loadFeatureGroups");
    await this.$store.dispatch("loadCategoryGroups");

    // if no settings => get config from API!
    if (this.initialSettings) {
      this.settings = this.initialSettings;
      this.showMap = this.settings?.showMap ?? false;
      // initial setting of search parameters - from external search
      const fromSearch = !!this.settings.from_search;
      if (fromSearch) {
        // reset certain searches that have not been defined in search form
        this.$store.commit("updateSelectedFeatures", []);
        if (this.$store.state.filterFavorites)
          this.$store.commit("toggleFilterFavorites");
      }

      if (this.settings.start) {
        this.$store.commit("setStart", this.settings.start);
      } else if (fromSearch) this.$store.commit("setStart", undefined);

      if (this.settings.end) {
        this.$store.commit("setEnd", this.settings.end);
      } else if (fromSearch) this.$store.commit("setEnd", undefined);

      if (this.settings.s) {
        this.$store.commit("setSearch", this.settings.s);
      } else if (fromSearch) this.$store.commit("setSearch", "");

      if (this.settings.categories) {
        this.$store.commit("selectCategories", this.settings.categories);
      } else if (fromSearch) this.$store.commit("selectCategories", []);

      this.$store.commit("setDistinctEvents", this.settings.distinctEvents);
    } else {
      await this.loadSettings();
    }

    // store default values in store
    this.$store.commit("setSettings", {
      sort: this.settings.defaultSorting,
      direction: this.settings.defaultDirection,
      distinctEvents: this.settings.distinctEvents
    });

    // set stuff
    if (this.$store.state.sort) {
      this.currentSorting =
        this.$store.state.sort +
        ":" +
        (this.$store.state.direction === "desc" ? "desc" : "asc");
    }

    // data defined or get from API?
    if (this.initialData && !this.$store.state.storedValuesExist) {
      // only load from T3 if no settings have been changed
      const data =
        typeof this.initialData === "string"
          ? JSON.parse(this.initialData)
          : this.initialData;
      this.setEventsDataFromSource(data, true);
      const nextPage = parseInt(this.initialNextPage);
      this.nextPage = isNaN(nextPage) ? -1 : nextPage;

      await this.fetchCountByCategory();
    } else {
      await this.loadData();
    }
    if (this.internInstruction) {
      this.headersIntern.push({
        text: "Interne Anweisung",
        value: "internInformation"
      });
    }
    if (this.internComment) {
      this.headersIntern.push({
        text: "Interner Kommentar",
        value: "internComment"
      });
    }
    if (this.accessInformation) {
      this.headersIntern.push({
        text: "Eintritt",
        value: "accessInformation"
      });
    }
    // loading finished
    this.isLoading = false;

    // localization
    this.$i18n.locale = this.$vuetify.lang.current.substring(0, 2);

    // set sortings
    this.sortings = [
      {
        text: this.$i18n.t("message.sort_moment"),
        value: "moment:asc"
      },
      {
        text: this.$i18n.t("message.sort_moment"),
        value: "moment:desc"
      },
      {
        text: this.$i18n.t("message.sort_title"),
        value: "title_asc:asc"
      },
      {
        text: this.$i18n.t("message.sort_title"),
        value: "title_asc:desc"
      },
      {
        text: this.$i18n.t("message.location"),
        value: "location:asc"
      },
      {
        text: this.$i18n.t("message.location"),
        value: "location:desc"
      }
    ];

    this.isCreating = false;

    this.widgetWidth = this.$refs?.theWidget?.offsetWidth;
    this.leftColWidth = this.$refs?.theLeftCol?.offsetWidth;
    await this.getLocations();
    this.hitsPerPage = this.settings.hitsPerPage;
  },

  computed: {
    cApiKey() {
      return this.apiKey || this.$store.state.apiKey;
    },
    niceDate() {
      if (this.event && this.event.moment && this.event.moment.from) {
        return niceDate(this.event.moment.from, this.$i18n.locale);
      }
      return "";
    },
    niceTime() {
      if (this.event && this.event.moment && this.event.moment.from) {
        return niceDate(this.event.moment.from, this.$i18n.locale, true);
      }
      return "";
    },

    // calculate cols
    cols() {
      if (this.widgetWidth >= 1920) return 2;
      if (this.widgetWidth >= 1260 && this.showMap) return 4;
      if (this.widgetWidth >= 1260 && !this.showMap) return 3;
      if (this.widgetWidth >= 960 && this.showMap) return 6;
      if (this.widgetWidth >= 960 && !this.showMap) return 4;
      return 6;
    },
    firstDayOfWeek() {
      return this.$store.state.firstDayOfWeek;
    },
    totalFavorites() {
      return this.$store.state.favorites.length;
    },
    selectedCategories() {
      return this.$store.state.selectedCategories;
    },
    selectedCategoryGroup() {
      return this.$store.state.selectedCategoryGroup;
    },
    selectedLocation() {
      return this.$store.state.selectedLocation;
    },
    selectedLocationName() {
      if (this.$store.state.selectedLocation && this.locationsWithCounts) {
        for (let i = 0; i < this.locationsWithCounts.length; i++) {
          if (
            this.locationsWithCounts[i]._id.toString() ===
            this.$store.state.selectedLocation.toString()
          ) {
            return this.locationsWithCounts[i].title;
          }
        }
      }
      return "???";
    },
    selectedFeatures() {
      return this.$store.state.selectedFeatures;
    },
    selectedCity() {
      return this.$store.state.selectedCity;
    },
    selectedNormalizedCity() {
      return this.$store.state.selectedNormalizedCity;
    },
    favoritesFiltered() {
      return this.$store.state.filterFavorites;
    },
    distinctEvents() {
      return this.$store.state.distinctEvents;
    },
    startEndDate() {
      if (
        this.$store.state.start &&
        this.$store.state.end &&
        this.$store.state.start !== this.$store.state.end
      ) {
        return (
          niceDate(
            this.$store.state.start,
            this.$i18n.locale,
            false,
            false,
            false
          ) +
          "–" +
          niceDate(
            this.$store.state.end,
            this.$i18n.locale,
            false,
            false,
            false
          )
        );
      }
      if (this.$store.state.start) {
        return niceDate(
          this.$store.state.start,
          this.$i18n.locale,
          false,
          false,
          false
        );
      }
      if (this.$store.state.end) {
        return niceDate(
          this.$store.state.end,
          this.$i18n.locale,
          false,
          false,
          false
        );
      }

      return false;
    },
    searchTerm() {
      return this.$store.state.s;
    },
    isMobile() {
      return this.widgetWidth < 600; // => https://vuetifyjs.com/de-DE/customization/breakpoints/
    },
    currentSortingTitle() {
      if (this.currentSorting && this.sortings && this.sortings.length) {
        for (let i = 0; i < this.sortings.length; i++) {
          if (this.sortings[i].value === this.currentSorting)
            return this.sortings[i].text;
        }
      }

      return this.$i18n.t("message.sortings");
    },
    maxMapHeight() {
      if (!this.$refs.header) return window.innerHeight;
      return window.innerHeight - this.$refs.header.clientHeight;
    }
  },
  methods: {
    getTitle(item) {
      if (
        this.showShortTitle &&
        item.moment.short_title &&
        this.showShortDescription &&
        item.moment.short_description
      )
        return `<br/> ${item.moment.short_title}<br/> ${item.moment.short_description}`;
      else if (this.showShortTitle && item.moment.short_title)
        return `<br/> ${item.moment.short_title}`;
      else if (this.showShortDescription && item.moment.short_description)
        return `<br/> ${item.moment.short_description}`;
      else if (
        this.showShortTitle &&
        item.short_title &&
        this.showShortDescription &&
        item.short_description
      )
        return `<br/> ${item.short_title}<br/> ${item.short_description}`;
      else if (this.showShortTitle && item.short_title)
        return `<br/> ${item.short_title}`;
      else if (this.showShortDescription && item.short_description)
        return `<br/> ${item.short_description}`;
    },
    getInternComment(item) {
      return item.comment;
    },
    getInternInformation(item) {
      return item.instructions;
    },
    getStartTime(item) {
      if (item.all_day) return "Ganztägig";
      else return moment(item.moment?.from ?? item.start).format("HH:mm");
    },
    createQuery(page = -1) {
      // offset set?
      if (this.settings.startOffset > 0) {
        page = 0;
      }

      // define query
      const query = {
        page: page < 0 ? this.settings.startPage : page,
        limit: this.settings.hitsPerPage,
        offset: this.settings.startOffset,
        highlight_to_top: this.settings.showHighlightToTop,
        sort:
          this.$store.state.sort || this.settings.defaultSorting || "moment",
        direction:
          this.$store.state.direction ||
          this.settings.defaultDirection ||
          "asc",
        distinct: this.$store.state.distinctEvents
      };

      if (this.selectedCity) query.city = this.selectedCity;
      if (this.selectedNormalizedCity)
        query.normalized_city = this.selectedNormalizedCity;

      // filter by favorites?
      if (this.$store.state.filterFavorites) {
        const ids = [];
        const moments = [];

        if (this.$store.state.favorites) {
          this.$store.state.favorites.forEach(id => {
            const parts = id.split(":");
            // add to moments or ids
            if (parts.length > 1) {
              moments.push(parts[1]);
            } else {
              ids.push(parts[0]);
            }
          });

          // only search for moments
          if (moments.length > 0) {
            query.moment_ids = moments;
          } else {
            query.ids = ids;
          }
        }
      } else {
        // general filters - only if no favorites are filtered
        query.active = this.settings.showReleased;
        query.hidden = 0;
        //overwrite query if data-highlight is 1
        if (this.highlight || this.settings.showHighlight === 1) {
          query.highlight = 1;
        }

        query.regionality = this.settings.regionality;
        query.location = this.$store.state.selectedLocation || undefined;
        query.feature_ids = this.$store.state.selectedFeatures
          ? this.$store.state.selectedFeatures.slice()
          : []; // clone array

        if (!this.$store.state.selectedCategoryGroup) {
          query.category_ids = [];
        } else if (this.$store.state.selectedCategories?.length) {
          query.category_ids = this.$store.state.selectedCategories.slice();
        } else {
          query.category_ids = this.$store.state.categories
            .filter(
              category =>
                category.category_group ===
                  this.$store.state.selectedCategoryGroup ||
                category._id === this.$store.state.selectedCategoryGroup
            )
            .map(category => category._id);
        }

        // start/end dates set
        if (this.$store.state.start) query.start = this.$store.state.start;
        if (this.$store.state.end) query.end = this.$store.state.end;

        // search term
        if (this.$store.state.s) query.s = this.$store.state.s;

        // no start/end set -> take default offset for date, if set to other value than -1000
        if (
          !query.start &&
          !query.end &&
          this.settings.startDateDifference !== -1000
        ) {
          // get date without tz offset
          const date = new Date();
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          // add number of days to set difference
          if (this.settings.startDateDifference !== 0) {
            date.setDate(date.getDate() + this.settings.startDateDifference);
          }

          const endDate = new Date();
          endDate.setMinutes(
            endDate.getMinutes() - endDate.getTimezoneOffset()
          );
          if (this.settings.endDateDifference) {
            endDate.setDate(
              endDate.getDate() + this.settings.endDateDifference
            );
          } else {
            endDate.setDate(date.getDate() + 365);
          }

          // set start
          if (validateDate(date))
            query.start = date.toISOString().substring(0, 10);
          if (validateDate(endDate))
            query.end = endDate.toISOString().substring(0, 10);
        }

        // manually contain category ids, if needed
        if (
          this.settings.contain_by_category_ids &&
          this.settings.contain_by_category_ids.length
        ) {
          query.contain_by_category_ids = this.settings.contain_by_category_ids;
        }

        // manually contain features
        if (
          this.settings.contain_by_feature_ids &&
          this.settings.contain_by_feature_ids.length
        ) {
          query.contain_by_feature_ids = this.settings.contain_by_feature_ids;
        }
      }
      if (this.$store.state.setDate) {
        query.start = this.$store.state.start || new Date().toISOString();
        query.end = this.$store.state.end;
      }
      return query;
    },
    async loadData(page = -1, resetData = true, filter) {
      let query = this.createQuery(page);
      const isCompact = this.settings?.outputFormat === "compact";
      if (isCompact || filter) {
        query.limit = filter?.limit ?? this.settings?.hitsPerPage;
      }
      try {
        const data = await findEvents(
          this.$store.state.apiUrl,
          this.cApiKey,
          query,
          true
        );

        this.setEventsDataFromSource(data, resetData, isCompact);
        await this.getLocations();

        await this.fetchCountByCategory();
      } catch (e) {
        this.error = e;
      }
    },
    async applyFilter(filter) {
      if (filter) {
        await this.loadData(filter.page, false, filter);
      }
    },
    setEventsDataFromSource(data, resetData, isCompact) {
      replaceArrayWithWebContent(data.results);
      // set data new or append
      this.events =
        resetData || isCompact
          ? data.results
          : this.events.concat(data.results);
      this.totalCount = data.totalCount;
      this.countByFeature = data.countByFeature || {};
      this.locationsWithCounts = data.locationsWithCounts || [];
      this.outputFormat = this.settings.outputFormat;
      this.showNotepad = this.settings.showNotepad;
      this.internComment = this.settings.internComment;
      this.internInstruction = this.settings.internInstruction;
      this.accessInformation = this.settings.accessInformation;
      this.showShortTitle = this.settings.showShortTitle;
      this.showShortDescription = this.settings.showShortDescription;
      this.disableFilter = this.settings.disableFilter;
      this.events.forEach(event => {
        event.groupStart = moment(event?.moment?.from ?? event?.start)
          .locale("de")
          .format("dddd, DD.MM.YYYY");
      });
      let { limit, offset, page } = data;

      this.totalCount = parseInt(this.totalCount) ?? 0;
      limit = parseInt(limit) ?? 0;
      offset = parseInt(offset) ?? 0;
      page = parseInt(page) ?? 0;

      this.nextPage = this.totalCount > limit + offset ? page + 1 : 0;
    },
    async getLocations() {
      const { locations } = await findLocations(
        this.$store.state.apiUrl,
        this.cApiKey,
        {
          limit: 1000
        }
      );
      let unsorted = flatten(locations);
      this.locations = unsorted.sort((a, b) =>
        a.address.normalized_city?.localeCompare(b.address.normalized_city)
      );
    },
    async fetchCountByCategory() {
      const query = this.createQuery();

      this.countByCategory = await findEvents(
        this.$store.state.apiUrl,
        this.cApiKey,
        query,
        false,
        true
      );
      const categories = this.$store.state.categories.filter(
        item => this.countByCategory[item._id]
      );

      const { selectedCategories } = this.$store.state;
      selectedCategories.forEach((selectedCategory, index) => {
        if (!categories.find(cat => cat._id === selectedCategory)) {
          selectedCategories.splice(index, 1);
        }
      });
      this.$store.commit("selectCategories", selectedCategories);
    },
    /**
     * load specific page and add results to list
     * @param page
     * @returns {Promise<void>}
     */
    async loadPage(page) {
      await this.loadData(page, false);
    },
    async loadSettings() {
      try {
        const data = await findConfig(this.$store.state.apiUrl, this.cApiKey);
        if (data && data.apiKeys && data.apiKeys.length) {
          this.settings = data.apiKeys[0]; // find first API key setting since this will be ours
          this.showMap = this.settings?.showMap ?? false;
        }
      } catch (e) {
        this.error = e;
      }
    },
    labelFromId(id) {
      return this.$store.state.labelsById[id] || "???";
    },
    onResize() {
      this.widgetWidth = this.$refs.theWidget?.offsetWidth ?? 0;
      this.leftColWidth = this.$refs.theLeftCol?.offsetWidth ?? 0;
      this.rightColWidth = this.$refs.theRightCol?.offsetWidth - 8 ?? 0; // Padding = 2*4px
    },
    removeSearchTerm() {
      this.$store.commit("setSearch", undefined);
    },
    removeStartEndDate() {
      this.$store.commit("setStartEnd", {
        start: undefined,
        end: undefined
      });
    },
    removeSelectedCategory(categoryId) {
      const selectedCategories = this.$store.state.selectedCategories;
      const pos = selectedCategories.indexOf(categoryId);
      if (pos > -1) {
        selectedCategories.splice(pos, 1);
        this.$store.commit("selectCategories", selectedCategories);
      }
    },
    removeSelectedCategoryGroup() {
      this.$store.commit("setSelectedCategoryGroup", "");
    },
    removeSelectedLocation() {
      this.$store.commit("selectLocation", undefined);
    },
    removeDistinctEvents() {
      this.$store.commit("setDistinctEvents", false);
    },
    removeSelectedFeature(featureId) {
      const selectedFeatures = this.$store.state.selectedFeatures;
      const pos = selectedFeatures.indexOf(featureId);
      if (pos > -1) {
        selectedFeatures.splice(pos, 1);
        this.$store.commit("updateSelectedFeatures", selectedFeatures);
      }
    },
    toggleFilterFavorites() {
      this.$store.commit("toggleFilterFavorites");
    },
    changeSorting(newValue = undefined) {
      if (newValue) this.currentSorting = newValue;

      const parts = this.currentSorting.split(":");
      if (parts.length === 2) {
        this.$store.commit("updateSort", {
          sort: parts[0],
          direction: parts[1]
        });
        // load now
        this.loadData();
      }
    }
  },
  watch: {
    selectedCategories() {
      if (!this.isCreating) this.loadData();
    },
    selectedLocation() {
      if (!this.isCreating) this.loadData();
    },
    selectedFeatures() {
      if (!this.isCreating) this.loadData();
    },
    favoritesFiltered() {
      if (!this.isCreating) this.loadData();
    },
    startEndDate() {
      if (!this.isCreating) this.loadData();
    },
    searchTerm() {
      if (!this.isCreating) this.loadData();
    },
    selectedCity() {
      if (!this.isCreating) this.loadData();
    },
    selectedNormalizedCity() {
      if (!this.isCreating) this.loadData();
    },
    selectedCategoryGroup() {
      if (!this.isCreating) this.loadData();
    },
    distinctEvents() {
      if (!this.isCreating) this.loadData();
    },
    totalFavorites(newValue) {
      // load, if we are in the favorites view
      if (this.$store.state.filterFavorites) {
        // if newValue == 0 -> reset filter
        if (newValue === 0) {
          this.$store.commit("toggleFilterFavorites");
        } else {
          this.loadData();
        }
      }
    },
    showMap(value) {
      if (value) {
        // trigger resize
        const that = this;
        setTimeout(function() {
          that.onResize();
        }, 150);
      }
    },
    showFilters(value) {
      document.body.style.overflowY = value ? "hidden" : "inherit";
    }
  },
  i18n
};
